import { setCustomTimeout } from "@client/lib/misc";
import { type GenericMap } from "@client/types";
import loader from "../loader";

export const ANIMATION_TYPE = {
  None: "none",
  Bounce: "bounce",
  Fade: "fade",
  Zoom: "zoom"
};

export const ANIMATION_DIRECTION = {
  Center: "Center",
  Right: "Right",
  Left: "Left",
  Up: "Up",
  Down: "Down"
};

export enum AnimationTrigger {
  Immediate = "immediate",
  Appear = "appear",
  PageLoad = "pageLoad"
}

export const APPEAR_ANIMATION_OPTIONS: AnimationOptions = {
  delay: "0s",
  direction: ANIMATION_DIRECTION.Center,
  duration: "300ms",
  isIn: true,
  noSpaceIfHidden: false,
  trigger: AnimationTrigger.Immediate,
  type: ANIMATION_TYPE.Fade
};

export interface AnimationOptions {
  type: string;
  delay: string;
  direction: string;
  noSpaceIfHidden: boolean;
  trigger: AnimationTrigger;
  isIn: boolean;
  duration: string;
}

export const NO_ANIMATION_OPTIONS: AnimationOptions = {
  type: ANIMATION_TYPE.None,
  noSpaceIfHidden: false,
  delay: "0ms",
  direction: ANIMATION_DIRECTION.Center,
  isIn: false,
  trigger: AnimationTrigger.Appear,
  duration: "0ms"
};

const ANIMATION_DURATIONS: GenericMap<number> = {
  [ANIMATION_TYPE.Bounce]: 750,
  [ANIMATION_TYPE.Fade]: 750,
  [ANIMATION_TYPE.Zoom]: 300
};

export default (element: HTMLElement, options: Partial<AnimationOptions>) => {
  if (!element) throw new Error("animate: element is required");

  if (options.type === ANIMATION_TYPE.None) return;

  const opts = {
    ...NO_ANIMATION_OPTIONS,
    ...options
  };

  const $elem = $(element);
  let animationType = `${opts.type}${opts.isIn ? "In" : "Out"}`;
  animationType = `${animationType}${opts.direction === ANIMATION_DIRECTION.Center ? "" : opts.direction}`;

  if ($elem.data("ANIMATION_TIMEOUT")) {
    clearTimeout($elem.data("ANIMATION_TIMEOUT"));
  }

  // remove any previously set animation classes
  Object.keys(ANIMATION_TYPE).forEach((key) =>
    ["", ...Object.keys(ANIMATION_DIRECTION)].forEach((d) =>
      $elem.removeClass(
        `${ANIMATION_TYPE[key]}In${ANIMATION_DIRECTION[d] || ""} ${ANIMATION_TYPE[key]}Out${
          ANIMATION_DIRECTION[d] || ""
        }`
      )
    )
  );

  $elem
    .addClass("animated")
    .css({ maxHeight: opts.isIn && opts.noSpaceIfHidden ? "0px" : "", opacity: opts.isIn ? "0" : "1" });

  const show = () => {
    const timeout = setCustomTimeout(() => {
      $elem.addClass(`${animationType}`);

      if (opts.noSpaceIfHidden) {
        if (opts.isIn) {
          $elem.animate(
            {
              maxHeight: $elem.get(0)!.scrollHeight
            },
            300,
            () => {
              $elem.css({ maxHeight: "", opacity: opts.isIn ? "1" : "0", animationDuration: opts.duration });
            }
          );
        } else {
          $elem.css({ opacity: opts.isIn ? "1" : "0", animationDuration: opts.duration });

          setTimeout(
            () =>
              $elem.animate(
                {
                  maxHeight: "0px"
                },
                300
              ),
            ANIMATION_DURATIONS[opts.type] - 300
          );
        }
      } else {
        $elem.css({ opacity: opts.isIn ? "1" : "0", animationDuration: opts.duration });
      }
    }, opts.delay);

    $elem.data("ANIMATION_TIMEOUT", timeout);
  };

  switch (opts.trigger) {
    case AnimationTrigger.Appear: {
      $elem.appear(
        show,
        {
          accX: 0,
          accY: 0
        },
        "easeInCubic"
      );
      break;
    }
    case AnimationTrigger.Immediate: {
      show();
      break;
    }
    case AnimationTrigger.PageLoad: {
      loader.promise.finally(show);
      break;
    }
  }
};
